/**
 * Chart Configuration Utilities
 * 
 * Highcharts configuration helpers for consistent chart styling and options.
 * 
 * NOTE: This is a template file. Replace with the actual chartConfig.ts
 * from the ORBCoN Tools app (app/utils/chartConfig.ts) for production use.
 */

import type { Options } from 'highcharts';
import type { ChartDataPoint } from '../api-client';

/**
 * Common chart options shared across all charts
 */
export const commonChartOptions: any = {
  backgroundColor: 'transparent',
  style: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
};

/**
 * Month names for label generation
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Generate correct month labels from chart data points
 * Fixes issue where API labels are one month behind the actual data period
 * @param data - Array of chart data points with month/year fields
 * @returns Array of corrected month labels (e.g., "April 2024", "May 2024", ...)
 */
export function generateCorrectMonthLabels(data: ChartDataPoint[]): string[] {
  return data.map((point) => {
    // Use month and year fields if available, otherwise parse the label
    let month: number;
    let year: number;

    if (point.month !== undefined && point.year !== undefined) {
      // Shift forward by 1 month to correct the offset
      month = point.month + 1;
      year = point.year;
      
      // Handle year rollover (December -> January of next year)
      if (month > 12) {
        month = 1;
        year += 1;
      }
    } else {
      // Fallback: parse the existing label and shift forward by 1 month
      // Expected format: "March 2024" or "Mar 2024"
      const labelMatch = point.label.match(/(\w+)\s+(\d{4})/);
      if (labelMatch) {
        const monthName = labelMatch[1];
        const monthIndex = MONTH_NAMES.findIndex(m => 
          m.toLowerCase().startsWith(monthName.toLowerCase())
        );
        if (monthIndex !== -1) {
          month = monthIndex + 2; // +1 for 0-indexed to 1-indexed, +1 for shift
          year = parseInt(labelMatch[2]);
          if (month > 12) {
            month = 1;
            year += 1;
          }
        } else {
          // If we can't parse, return the original label
          return point.label;
        }
      } else {
        // If we can't parse, return the original label
        return point.label;
      }
    }

    // Generate the corrected label
    const monthName = MONTH_NAMES[month - 1];
    return `${monthName} ${year}`;
  });
}

/**
 * Get common X-axis configuration
 */
export function getCommonXAxis(categories: string[]): any {
  return {
    categories,
    title: {
      text: 'Month',
    },
    labels: {
      rotation: -45,
      style: {
        fontSize: '11px',
        color: '#6b7280',
        fontWeight: '500',
      },
    },
    lineColor: '#e5e7eb',
    tickColor: '#e5e7eb',
  };
}

/**
 * Get volume Y-axis configuration (left axis)
 */
export function getVolumeYAxis(title: string = 'Volume'): any {
  return {
    title: {
      text: title,
    },
    labels: {
      style: {
        fontSize: '11px',
        color: '#6b7280',
        fontWeight: '500',
      },
      formatter: function (this: { value?: number | string }) {
        const val = typeof this.value === 'number' ? this.value : 0;
        return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toString();
      },
    },
    opposite: false,
    gridLineColor: '#f1f5f9',
  };
}

/**
 * Get rate Y-axis configuration (right axis)
 * Returns a single axis config for the rate axis (right side)
 */
export function getRateYAxis(title: string = 'Rate (%)'): any {
  return {
    title: {
      text: title,
    },
    labels: {
      style: {
        fontSize: '11px',
        color: '#6b7280',
        fontWeight: '500',
      },
      formatter: function (this: { value?: number | string }) {
        return `${this.value}%`;
      },
    },
    opposite: true,
    gridLineColor: '#f1f5f9',
  };
}

/**
 * Get common tooltip configuration
 * @param getLabel - Optional function to get label for a point index
 */
export function getCommonTooltip(getLabel?: (pointIndex: number) => string): Partial<Options> {
  return {
    tooltip: {
      backgroundColor: 'white',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      shadow: { offsetX: 0, offsetY: 4, width: 8, opacity: 0.1 },
      style: { fontSize: '12px', color: '#374151' },
      shared: true,
      useHTML: true,
      formatter: function (this: any) {
        const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? this.point?.index ?? this.x ?? 0;
        const label = getLabel ? getLabel(pointIndex) : this.x;
        let tooltip = `<b>${label}</b><br/>`;
        this.points?.forEach((point: any) => {
          tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
        });
        return tooltip;
      },
    },
  };
}

/**
 * Axis label style
 */
export const axisLabelStyle = {
  fontSize: '11px',
  color: '#6b7280',
  fontWeight: '500' as const,
};

/**
 * Create trendline series configuration
 */
export function createTrendlineSeries(
  trendResult: { trendline: number[] } | number[],
  color: string = '#666666',
  lineWidth: number = 2,
  name: string = 'Trendline'
): any {
  const data = Array.isArray(trendResult) ? trendResult : trendResult.trendline;
  return {
    type: 'line',
    name,
    data,
    color,
    dashStyle: 'Dash',
    lineWidth,
    marker: {
      enabled: false,
    },
    enableMouseTracking: false,
  };
}

/**
 * Create upper confidence band series
 */
export function createUpperBandSeries(
  trendResult: { upperBand: number[] } | number[],
  color: string = '#cccccc',
  name: string = 'Upper Band'
): any {
  const data = Array.isArray(trendResult) ? trendResult : trendResult.upperBand;
  return {
    type: 'line',
    name,
    data,
    color,
    dashStyle: 'Dot',
    marker: {
      enabled: false,
    },
    enableMouseTracking: false,
    fillOpacity: 0.1,
  };
}

/**
 * Create lower confidence band series
 */
export function createLowerBandSeries(
  trendResult: { lowerBand: number[] } | number[],
  color: string = '#cccccc',
  name: string = 'Lower Band'
): any {
  const data = Array.isArray(trendResult) ? trendResult : trendResult.lowerBand;
  return {
    type: 'line',
    name,
    data,
    color,
    dashStyle: 'Dot',
    marker: {
      enabled: false,
    },
    enableMouseTracking: false,
    fillOpacity: 0.1,
  };
}

/**
 * Line chart plot options
 */
export const linePlotOptions: any = {
  marker: {
    radius: 4,
    symbol: 'circle',
  },
  lineWidth: 2,
};

/**
 * Column chart plot options
 */
export const columnPlotOptions: any = {
  borderWidth: 0,
  groupPadding: 0.1,
  pointPadding: 0.05,
};

/**
 * Area chart plot options
 */
export const areaPlotOptions: any = {
  stacking: 'normal',
  marker: {
    enabled: false,
  },
  lineWidth: 1,
};
